import { memo, useContext, useMemo, type FunctionComponent } from 'react';
// Material UI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
// Skillmore UI Components
import CardTitle from '@empathco/ui-components/src/elements/CardTitle';
import CardsGrid from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import { TalentEmployeeObject, DevPlanEmployee } from '../graphql/customTypes';
import { GlobalContext } from '../context/global';
import CohortAvatar from '../elements/CohortAvatar';
import EmployeeCard, { EmployeeCardItem, EmployeeCardProps } from '../v3/EmployeeCard';
// SCSS imports
import { shadyBg } from '@empathco/ui-components/src/styles/modules/ItemsGrid.module.scss';
import { titlePadding } from './DevPlanCohort.module.scss';

type DevPlanCohortProps = {
  readonly title?: string | null;
  readonly employees?: DevPlanEmployee[] | TalentEmployeeObject[] | null;
  readonly withMatchRate?: boolean;
  readonly disabled?: boolean | null;
  readonly pending?: boolean | null;
  readonly failed?: boolean | null;
}

const DevPlanCohort: FunctionComponent<DevPlanCohortProps> = ({
  title,
  employees,
  withMatchRate = false,
  disabled = false,
  pending = false,
  failed = false
}) => {
  const { paths: { supvEmplPath } } = useContext(GlobalContext);

  const componentProps: Partial<EmployeeCardProps> = useMemo(() => ({
    ...withMatchRate ? { matchRateVariant: 'planned' } : {},
    avatarVariant: 'grey',
    route: supvEmplPath,
    withDetailsPopup: true
  }), [supvEmplPath, withMatchRate]);

  return (
    <Box color="text.label" className={shadyBg}>
      <CardTitle
          title={(
            <Typography variant="subtitle1" component="span">
              <Box color="secondary.main" component="span">
                {title}
              </Box>
            </Typography>
          )}
          avatar={<CohortAvatar/>}
          className={(pending && !employees) || failed ? undefined : titlePadding}
      />
      <CardsGrid
          items={employees as (EmployeeCardItem[] | null | undefined)}
          variant="white"
          withReloading
          notFoundMessage="hr.cohort.empty"
          withoutTopPadding
          disabled={disabled}
          pending={pending}
          failed={failed}
          component={EmployeeCard}
          ComponentProps={componentProps}
      />
    </Box>
  );
};

export default memo(DevPlanCohort);
